import React, {
  useContext, useState, useMemo, useEffect, useRef
} from 'react';
import proptypes from 'prop-types';
import classNames from 'classnames';
import { Cart, Compare, TrashCan, Duplicate } from '@one-thd/sui-icons';
import { Button } from '@one-thd/sui-atomic-components';
import {
  extend,
  useDataModel,
  arrayOf,
  number as numberType,
  string as stringType,
  shape as shapeType,
  customType,
  params
} from '@thd-nucleus/data-sources';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import { AddTo } from '@thd-olt-component-react/add-to';
import { AddToCart } from '@thd-olt-component-react/add-to-cart';
import { ListSettings, EmailList } from '@thd-olt-component-react/list-forms';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { ProductListContext } from '../ProductListContext';
import {
  getAtcEnabledProducts,
  getProductsFromGroups,
  isPureIntentItem,
  getSelectedCatalogProducts,
  getSelectedProducts,
  isProductInGroup,
  createIntentGroups
} from '../productListHelper';
import { ActionBarButton } from '../action-bar-button/ActionBarButton';
import { TOO_MANY_PRODUCTS_MESSAGE, QUANTITY_LIMIT, NO_ITEMS_SELECTED_ATC_MESSAGE } from './constants';
import { LoadingDrawer } from '../loading-drawer/LoadingDrawer';
import { AddToMobile } from './AddToMobile';
import { DeleteModal } from '../delete-modal/DeleteModal';

const { bool, shape, func, string } = proptypes;
// TODO: figure out where to add an alert for issues.

export const ActionBar = ({
  showActionBar,
  sticky,
  selectedItemsByGroup,
  onGroupAccordionSelectionChange,
  errorSelectionEmpty,
  updateErrorSelectionEmpty
}) => {
  const { channel } = useContext(ExperienceContext);
  const store = useStore({ varnish: true });
  const isMobile = channel === 'mobile';
  const {
    isSharedList,
    list,
    productsDisplayed,
    setProductsDisplayed,
    // Product data management for ATC functionality
    productData,
    // Add Materials functionality
    onSuccesfulMaterialsAdd,
    refetch,
    onEditSuccess
  } = useContext(ProductListContext);

  // listOperations hook for intent creation (following ListView pattern)
  const [listOperations] = useDataModel('listOperations', {
    variables: {
      id: list?.id
    },
    fetchPolicy: 'no-cache',
    context: { withAuth: true },
    lazy: true
  });

  const [isAllSelected, setIsAllSelected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [addToType, setAddToType] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Extract all products from groups using helper function
  const products = useMemo(() => getProductsFromGroups(list), [list]);

  // Materials data for Add Materials (following ListView pattern)
  const materials = useMemo(() => {
    const productsMap = new Map(productsDisplayed.map(p => [p.itemId, p]));
    
    return list?.groups?.map((group) => {
      return {
        groupId: group.groupId,
        groupName: group.groupName,
        items: group.items?.map(((item) => {
          const productInfo = item.itemId ? productsMap.get(item.itemId) || null : null;
          const itemData = {
            itemId: item.itemId,
            intent: item.intent,
            quantity: item.quantity,
            name: null,
            brand: null
          };
          if (productInfo) {
            itemData.name = productInfo?.identifiers?.productLabel;
            itemData.brand = productInfo?.identifiers?.brandName;
          }
          return itemData;
        }))
      };
    });
  }, [list, productsDisplayed]);

  // Handler for intent changes from AI List Builder (following ListView pattern)
  const handleIntentsChange = (intentGroups) => {
    return createIntentGroups({
      intentGroups,
      list,
      listOperations,
      refetch,
      onEditSuccess
    });
  };

  // Create filtered version without pure intents for catalog operations
  const catalogProducts = useMemo(() => {
    return products.filter((product) => !isPureIntentItem(product));
  }, [products]);

  const { isB2BCustomer = false } = useThdCustomer();

  const buttonRef = useRef(null);

  // Extract selected group IDs and item IDs from GroupAccordion selections
  const groupAccordionSelections = useMemo(() => {
    if (!selectedItemsByGroup) {
      return { groupIds: [], itemIds: [] };
    }

    const groupIds = [];
    const itemIds = [];

    Object.keys(selectedItemsByGroup).forEach((groupId) => {
      const group = selectedItemsByGroup[groupId];

      // If entire group is selected (and not indeterminate), add group ID
      if (group?.isSelected && !group?.isIndeterminate) {
        groupIds.push(groupId);
      }

      // If individual items are selected within the group, add item IDs
      if (group?.items && group.items.length > 0) {
        itemIds.push(...group.items);
      }
    });

    return { groupIds, itemIds };
  }, [selectedItemsByGroup]);

  // Memoized collection of all selected catalog products (used by multiple calculations)
  const selectedCatalogProducts = useMemo(() => {
    return getSelectedCatalogProducts({ groupAccordionSelections, catalogProducts });
  }, [groupAccordionSelections, catalogProducts]);

  // Memoized collection of all selected products including intents (for Add to List)
  const selectedAllProducts = useMemo(() => {
    return getSelectedProducts({ groupAccordionSelections, allProducts: products });
  }, [groupAccordionSelections, products]);

  useEffect(() => {
    const { groupIds, itemIds } = groupAccordionSelections;
    const totalSelectedItems = itemIds.length + groupIds.reduce((count, groupId) => {
      // Count all items in selected groups
      const groupProducts = products.filter((product) => isProductInGroup(product, groupId));
      return count + groupProducts.length;
    }, 0);
    if (totalSelectedItems === 0 && isAllSelected) {
      // Trigger deselect-all analytics when transitioning from all-selected to not-all-selected
      window.LIFE_CYCLE_EVENT_BUS.trigger('product-list.deselect-all', {});
    }

    if (products.length && totalSelectedItems === products.length) {
      setIsAllSelected(true);
    } else if (
      (totalSelectedItems === 0 || totalSelectedItems !== products.length)
      && isAllSelected
    ) {
      setIsAllSelected(false);
    }
  }, [groupAccordionSelections, products, isAllSelected]);

  let baseUrl = '';
  if (typeof window !== 'undefined') {
    baseUrl = window.origin;
  }

  // Function to get product data with quantity (following ProjectATC pattern)
  const getProductDataWithQuantity = (items) => {
    return items.map((item) => {
      const atcProduct = productsDisplayed?.find((product) => product.itemId === item.itemId) || {};
      return {
        ...atcProduct,
        quantity: item.quantity
      };
    }).filter((item) => item?.itemId);
  };

  const cartReqParams = useMemo(() => {
    if (selectedCatalogProducts.length > 0) {
      // Get product data with quantity
      const selectedProducts = getProductDataWithQuantity(selectedCatalogProducts);

      const atcEnabledProducts = getAtcEnabledProducts({
        products: selectedProducts,
        store
      });

      return atcEnabledProducts;
    }
    return [];
  }, [selectedCatalogProducts, productsDisplayed, store]);

  // Calculate selected item count from GroupAccordion selections
  const selectedItemCount = useMemo(() => {
    const { groupIds, itemIds } = groupAccordionSelections;
    return itemIds.length + groupIds.reduce((count, groupId) => {
      // Count all items in selected groups (excluding items already counted individually)
      const group = selectedItemsByGroup[groupId];
      if (group?.isSelected && !group?.isIndeterminate) {
        // Find all products in this group to count them
        const groupProducts = products.filter((product) => isProductInGroup(product, groupId));
        return count + groupProducts.length;
      }
      return count;
    }, 0);
  }, [groupAccordionSelections, selectedItemsByGroup, products]);

  const isComparable = useMemo(() => {
    // Only comparable if we have 2-4 catalog items
    return selectedCatalogProducts.length > 1 && selectedCatalogProducts.length < 5;
  }, [selectedCatalogProducts]);

  const iconsContainer = classNames('sui-flex', {
    'sui-gap-1 sui-pr-0 sui-pl-0 sui-pb-4 sui-pt-2': isMobile,
    'sui-gap-2 sui-p-6 sui-pr-0': !isMobile
  });

  const stickyIconsContainer = classNames(
    'sui-flex', 'sui-w-full',
    { 'sui-gap-1 sui-justify-center': isMobile, 'sui-gap-2': !isMobile }
  );

  // Mobile button styles matching project-details exactly
  const copyToMobileButtonStyles = classNames(
    'sui-btn',
    'sui-btn-tertiary',
    'sui-btn-tertiary-focus',
    'sui-w-full',
    'sui-btn-tertiary-hover',
    'sui-btn-tertiary-active',
    'sui-leading-tight',
    'sui-tracking-normal',
    'sui-normal-case',
    'sui-line-clamp-unset',
    {
      'sui-text-xs': sticky,
      'sui-font-bold': sticky,
      'sui-text-base': !sticky,
      'sui-whitespace-nowrap': !sticky,
      'sui-font-regular': !sticky,
      'sui-flex': !sticky,
      'sui-gap-1': !sticky,
      'sm:sui-w-fit': !sticky,
    }
  );

  const quoteMobileButtonStyles = classNames(
    'sui-btn',
    'sui-btn-secondary',
    'sui-btn-secondary-focus',
    'sui-w-full',
    'sui-btn-secondary-active',
    'sui-btn-secondary-hover',
    'sui-leading-normal',
    'sui-tracking-normal',
    'sui-normal-case',
    'sui-line-clamp-unset',
    'sui-font-bold',
    {
      'sui-text-base': !sticky,
      'sui-text-xs': sticky,
      'sm:sui-w-fit': !sticky,
    }
  );

  const addMaterialsMobileButtonStyles = classNames(
    'sui-btn',
    'sui-btn-primary',
    'sui-btn-primary-focus',
    'sui-w-full',
    'sui-btn-primary-active',
    'sui-btn-primary-hover',
    'sui-leading-normal',
    'sui-tracking-normal',
    'sui-normal-case',
    'sui-line-clamp-unset',
    'sui-font-bold',
    {
      'sui-text-base': !sticky,
      'sui-text-xs': sticky,
    }
  );

  const onCompareClick = () => {
    // Trigger analytics for compare
    global.LIFE_CYCLE_EVENT_BUS.trigger('product-list.compare', {});

    const catalogItemIds = selectedCatalogProducts.map((item) => item.itemId);

    if (catalogItemIds.length > 1 && catalogItemIds.length < 5) {
      const queryparams = catalogItemIds
        .map((item, idx) => `item${idx + 1}=${item}`)
        .join('&');
      window.location.href = `${baseUrl}/compare?${queryparams}`;
    }
  };

  const onATCSuccess = (obj) => {
    const analyticsItemsData = selectedCatalogProducts.map((item) => {
      const displayedProducts = productsDisplayed || productData || [];
      return {
        itemId: item.itemId,
        index: displayedProducts.findIndex((pd) => pd.itemId === item.itemId)
      };
    });

    LIFE_CYCLE_EVENT_BUS.trigger('product-list.atc', { products: analyticsItemsData });
  };

  const fetchedItems = useMemo(() => {
    return selectedCatalogProducts.map((item) => {
      const newQuantity = item.quantity > QUANTITY_LIMIT ? QUANTITY_LIMIT : item.quantity;

      const fetchedItem = productsDisplayed?.find((product) => product.itemId === item.itemId)
        || productData?.find((product) => product.itemId === item.itemId);

      if (!fetchedItem) return null;

      return {
        ...fetchedItem,
        quantity: newQuantity
      };
    }).filter(Boolean);
  }, [selectedCatalogProducts, productsDisplayed, productData]);

  const arrayOfSelectedItems = useMemo(() => {
    return selectedCatalogProducts.map((item) => {
      const newQuantity = item.quantity > QUANTITY_LIMIT ? QUANTITY_LIMIT : item.quantity;

      return {
        itemId: item.itemId,
        quantity: newQuantity,
        itemType: 'catalog'
      };
    });
  }, [selectedCatalogProducts]);

  // Products for Add to List - includes both catalog items and intents
  const arrayOfSelectedListItems = useMemo(() => {
    return selectedAllProducts.map((item) => {
      const newQuantity = item.quantity > QUANTITY_LIMIT ? QUANTITY_LIMIT : item.quantity;
      const isIntent = isPureIntentItem(item);

      if (isIntent) {
        return {
          intent: item.intent || item.intentId,
          quantity: newQuantity,
          itemType: 'intent'
        };
      }

      return {
        itemId: item.itemId,
        quantity: newQuantity,
        itemType: 'catalog'
      };
    });
  }, [selectedAllProducts]);
  const addToProducts = useMemo(() => {
    return fetchedItems?.map((fetchedItem) => {
      if (!fetchedItem) return null;
      return {
        itemId: fetchedItem?.itemId,
        price: fetchedItem?.pricing?.value || null,
        productData: { info: fetchedItem?.info, identifiers: fetchedItem?.identifiers, pricing: fetchedItem?.pricing },
        quantity: fetchedItem?.quantity,
        sku: {
          storeSkuNumber: fetchedItem?.identifiers?.storeSkuNumber || null,
          omsThdSku: fetchedItem?.identifiers?.omsThdSku || null,
          specialOrderSku: fetchedItem?.identifiers?.specialOrderSku || null
        },
        itemType: 'catalog'
      };
    }).filter(Boolean);
  }, [fetchedItems]);
  const onLoadingSuccess = () => {
    setIsLoading(false);

    if (addToType === 'Cart') {
      window.LIFE_CYCLE_EVENT_BUS.trigger('cart.add-to-cart', {
        cartReqParams,
        cartOptions: {
          channel,
          host: typeof window !== 'undefined' ? window.origin : 'https://www.homedepot.com',
          misship: false,
          paypal: false
        },
        onSuccess: onATCSuccess
      });
    }

    if (addToType === 'Quote') {
      buttonRef.current.click();
    }

    if (addToType === '...') {
      buttonRef.current.firstChild.click();
    }

    setAddToType('');
  };

  const needToLoadProducts = useMemo(() => {
    // Check if catalog items are loaded in productsDisplayed
    const catalogItemIds = selectedCatalogProducts.map((item) => item.itemId);
    const displayedProducts = productsDisplayed || productData || [];
    const displayedItemIds = displayedProducts.map((item) => item?.itemId);
    const unloadedItems = catalogItemIds.filter((id) => !displayedItemIds.includes(id));

    return unloadedItems.length > 0;
  }, [selectedCatalogProducts, productsDisplayed, productData]);

  const shouldShowLoadingScreen = isLoading && needToLoadProducts;

  // Handle loading completion in useEffect to avoid setState during render
  useEffect(() => {
    if (isLoading && !needToLoadProducts) {
      setIsLoading(false);
      onLoadingSuccess();
    }
  }, [isLoading, needToLoadProducts]);

  const handleAddToClick = (onClickFunc, type) => {
    if (needToLoadProducts) {
      setAddToType(type);
      setIsLoading(true);
    } else {
      onClickFunc();
    }
  };

  const handleProductCountCheck = (onClickFunc) => {
    if (selectedItemCount > 89) {
      // TODO: Uncomment the below line when the alert is ready
      // setShowProductSelectionAlert(true);
      window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.errorAnalytics', {
        error: [{
          type: 'advisory',
          displayType: 'inline',
          pageSection: 'list details',
          message: TOO_MANY_PRODUCTS_MESSAGE,
          code: 'n/a',
        }]
      });
    }

    handleAddToClick(onClickFunc, 'Quote');
  };

  const updateLoadedProductsData = (newProductData, productId) => {
    const isArray = Array.isArray(newProductData);
    if (isArray) {
      setProductsDisplayed((prevData) => {
        // Filter out products that are already loaded to prevent duplicates
        const existingItemIds = new Set(prevData.map((item) => item.itemId));
        const newProducts = newProductData.filter((product) => !existingItemIds.has(product.itemId));

        // Only update if there are new products to add
        if (newProducts.length > 0) {
          return [...prevData, ...newProducts];
        }

        return prevData;
      });
    }
  };

  // Check if any groups or items are selected that contain actual catalog items
  const hasGroupAccordionSelections = useMemo(() => {
    // Only enable buttons if there are actual catalog items selected
    return selectedCatalogProducts.length > 0;
  }, [selectedCatalogProducts]);

  // Check if any items are selected for Add to List (includes intents)
  const hasListSelections = useMemo(() => {
    return selectedAllProducts.length > 0;
  }, [selectedAllProducts]);

  const handleATCClick = (onClick) => {
    // Check if any items are selected first (before ATC-filtering)
    if (!selectedCatalogProducts?.length) {
      updateErrorSelectionEmpty(NO_ITEMS_SELECTED_ATC_MESSAGE);
      return;
    }
    if (errorSelectionEmpty) updateErrorSelectionEmpty(null);
    // Let AddToCart handle items without fulfillment options appropriately
    handleAddToClick(onClick, 'Cart');
  };

  // Separate logic for delete button - can delete empty groups (except Default Group)
  const hasDeleteableSelections = useMemo(() => {
    const { groupIds, itemIds } = groupAccordionSelections;

    // Can delete if there are individual items selected
    if (itemIds.length > 0) {
      return true;
    }

    // Can delete if there are groups selected (with or without items), except Default Group
    if (groupIds.length > 0) {
      // Check if any selected group is NOT the Default Group
      const hasNonDefaultGroup = groupIds.some((groupId) => groupId !== '__ungrouped__' && groupId !== null
      );

      if (hasNonDefaultGroup) {
        return true;
      }

      // If only Default Group is selected, check if it has items
      if (groupIds.includes('__ungrouped__') || groupIds.includes(null)) {
        const defaultGroupProducts = (products).filter((product) => (
          !product.groupId || product.groupId === '__ungrouped__'
        ));
        return defaultGroupProducts.length > 0;
      }
    }

    return false;
  }, [groupAccordionSelections, products]);

  const noSelectedItems = !hasGroupAccordionSelections;
  const noListSelectedItems = !hasListSelections;
  const noDeleteableItems = !hasDeleteableSelections;

  if (!showActionBar) return null;

  const actionBarClasses = classNames('sui-flex', 'sui-gap-1', {
    'sui-flex-1': sticky && isMobile,
    'sui-justify-center': !sticky && isMobile
  });

  return (
    <div
      data-testid={sticky ? 'sticky-action-bar' : 'list-action-bar'}
      className={actionBarClasses}
    >
      <div className={sticky ? stickyIconsContainer : iconsContainer}>
        {!isMobile && (
          <>
            {/* Trashcan - icon only */}
            {!isSharedList && (
              <div>
                <ActionBarButton
                  ariaLabel="Delete"
                  Icon={TrashCan}
                  variant="tertiary"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={noDeleteableItems}
                />
              </div>
            )}
            {/* Compare */}
            <div>
              <ActionBarButton
                ariaLabel="Compare"
                Icon={Compare}
                variant="tertiary"
                onClick={onCompareClick}
                disabled={!isComparable}
                text="Compare"
              />
            </div>
            {/* Copy To - replaces Add to List and Add to Project */}
            <div>
              <AddTo
                addToFallbackType={null}
                addToType={null}
                showSelectionScreen
                loadedProductsData={productsDisplayed}
                products={addToProducts}
                updateLoadedProductsData={updateLoadedProductsData}
                CustomAddToActionElement={({ onClick }) => (
                  <Button
                    aria-label="Copy to"
                    disabled={noSelectedItems}
                    onClick={onClick}
                    variant="tertiary"
                    startIcon={Duplicate}
                    type="button"
                  >
                    Copy to
                  </Button>
                )}
              />
            </div>
            {/* Quote - secondary orange button (B2B only) */}
            {isB2BCustomer && (
              <div>
                <AddTo
                  addToType="Quote"
                  CustomAddToActionElement={({ onClick }) => (
                    <Button
                      aria-label="Add To Quote"
                      disabled={noSelectedItems}
                      onClick={() => handleProductCountCheck(onClick)}
                      variant="secondary"
                      ref={buttonRef}
                      type="button"
                    >
                      Quote
                    </Button>
                  )}
                  loadedProductsData={productsDisplayed}
                  products={arrayOfSelectedItems}
                  updateLoadedProductsData={updateLoadedProductsData}
                />
              </div>
            )}
            {/* Add to Cart */}
            <AddToCart
              cartReqParams={cartReqParams}
              cartOptions={{
                channel,
                host: typeof window !== 'undefined' ? window.origin : 'https://www.homedepot.com',
                misship: false,
                paypal: false
              }}
              onSuccess={onATCSuccess}
              customATCButtonRenderFunction={({ onClick }) => (
                <ActionBarButton
                  ariaLabel="Add to Cart"
                  text="Add to Cart"
                  onClick={() => handleATCClick(onClick)}
                  variant="secondary"
                />
              )}
            />
            {/* Add Materials - primary button (sticky bar only) */}
            {sticky && (
              <div>
                <AddTo
                  addToType="List"
                  list={{
                    listId: list?.id,
                    onMaterialsAdded: onSuccesfulMaterialsAdd,
                    isMaterialsButton: true
                  }}
                  CustomAddToActionElement={({ onClick }) => (
                    <Button
                      aria-label="Add Materials"
                      variant="primary"
                      onClick={onClick}
                      disabled={isSharedList}
                      type="button"
                    >
                      Add Materials
                    </Button>
                  )}
                  products={[]}
                  aiListBuilderData={{
                    onNewIntentsSave: handleIntentsChange,
                    title: list?.name,
                    materials,
                    refType: 'LIST',
                    refId: list?.id
                  }}
                />
              </div>
            )}
          </>
        )}
        {isMobile && (
          <>
            {/* Trashcan - icon only (left side) */}
            {!isSharedList && (
              <div className="sui-btn-tertiary sui-btn-tertiary-active sui-flex sui-items-center">
                <button
                  type="button"
                  aria-label="Delete"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={noDeleteableItems}
                >
                  <TrashCan size="small" />
                </button>
              </div>
            )}
            {/* Compare - icon only (static bar only) */}
            {!sticky && (
              <div className="sui-btn-tertiary sui-btn-tertiary-active sui-flex sui-items-center">
                <button
                  type="button"
                  aria-label="Compare"
                  onClick={onCompareClick}
                  disabled={!isComparable}
                >
                  <Compare size="small" />
                </button>
              </div>
            )}
            {/* Copy To */}
            <div className="sui-flex-1">
              <AddToMobile
                addToProducts={addToProducts}
                addToListProducts={arrayOfSelectedListItems}
                handleAddToClick={handleAddToClick}
                needToLoadProducts={needToLoadProducts}
                noSelectedItems={noSelectedItems}
                noListSelectedItems={noListSelectedItems}
                updateLoadedProductsData={updateLoadedProductsData}
                ref={buttonRef}
                selectedItemCount={selectedItemCount}
                isStickyBar={sticky}
                copyToMobileButtonStyles={copyToMobileButtonStyles}
              />
            </div>
            {/* Quote - secondary button (B2B only) */}
            {isB2BCustomer && (
              <div className="sui-flex-1">
                <AddTo
                  addToType="Quote"
                  CustomAddToActionElement={({ onClick }) => (
                    <button
                      aria-label="Add To Quote"
                      disabled={noSelectedItems}
                      type="button"
                      onClick={() => handleProductCountCheck(onClick)}
                      className={quoteMobileButtonStyles}
                    >
                      Quote
                    </button>
                  )}
                  loadedProductsData={productsDisplayed}
                  products={arrayOfSelectedItems}
                  updateLoadedProductsData={updateLoadedProductsData}
                />
              </div>
            )}
            {/* Add to Cart - icon only */}
            <AddToCart
              cartReqParams={cartReqParams}
              cartOptions={{
                channel,
                host: typeof window !== 'undefined' ? window.origin : 'https://www.homedepot.com',
                misship: false,
                paypal: false
              }}
              onSuccess={onATCSuccess}
              customATCButtonRenderFunction={({ onClick }) => {
                return (
                  <div className="sui-btn sui-btn-secondary sui-btn-secondary-focus sui-btn-secondary-active">
                    <button
                      type="button"
                      aria-label="Add to Cart"
                      onClick={() => handleATCClick(onClick)}
                    >
                      <Cart size="small" />
                    </button>
                  </div>
                );
              }}
            />
            {/* Add Materials - primary button (sticky bar only) */}
            {sticky && (
              <div className="sui-flex-1">
                <AddTo
                  addToType="List"
                  list={{
                    listId: list?.id,
                    onMaterialsAdded: onSuccesfulMaterialsAdd,
                    isMaterialsButton: true
                  }}
                  CustomAddToActionElement={({ onClick }) => (
                    <button
                      aria-label="Add Materials"
                      type="button"
                      onClick={onClick}
                      className={addMaterialsMobileButtonStyles}
                      disabled={isSharedList}
                    >
                      Add Materials
                    </button>
                  )}
                  products={[]}
                  aiListBuilderData={{
                    onNewIntentsSave: handleIntentsChange,
                    title: list?.name,
                    materials,
                    refType: 'LIST',
                    refId: list?.id
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
      <LoadingDrawer
        arrayOfSelectedItems={arrayOfSelectedItems}
        onClose={() => setIsLoading(false)}
        onLoadingSuccess={onLoadingSuccess}
        open={shouldShowLoadingScreen}
        title={`Add To ${addToType}`}
      />
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        selectedItems={groupAccordionSelections.itemIds}
        selectedGroups={groupAccordionSelections.groupIds}
      />
    </div>
  );
};

ActionBar.displayName = 'ProductList-ActionBar';
ActionBar.dataModel = extend(
  {
    listOperations: params({
      operationName: customType('Operation').enum(
        ['Delete', 'RemoveItems', 'UpdateItems', 'Create', 'Update', 'AddItems'], 'Create'),
      id: stringType().isRequired(),
      groups: arrayOf(customType('GroupInput').shape({
        groupId: stringType(),
        groupName: stringType(),
        groupOrder: stringType(),
        products: arrayOf(customType('ProductInput').shape({
          itemId: stringType(),
          quantity: numberType(),
          itemOrder: numberType(),
          comments: stringType(),
          intent: stringType()
        }))
      })),
      name: stringType(),
      listAccessType: customType('AccessTypes').enum(['Shared', 'Private'])
    }).mutation().shape({
      id: stringType(),
      groups: arrayOf(shapeType({
        groupId: stringType(),
        status: stringType(),
        items: arrayOf(shapeType({
          itemId: stringType(),
          quantity: numberType(),
          itemOrder: numberType(),
          comments: stringType(),
          status: stringType(),
          intent: stringType(),
          intentId: stringType()
        }))
      }))
    }),
  },
  AddTo,
  AddToCart,
  EmailList,
  ListSettings
);

ActionBar.propTypes = {
  showActionBar: bool,
  sticky: bool,
  selectedItemsByGroup: shape({}),
  onGroupAccordionSelectionChange: func,
  errorSelectionEmpty: string,
  updateErrorSelectionEmpty: func
};

ActionBar.defaultProps = {
  showActionBar: true,
  sticky: false,
  selectedItemsByGroup: {},
  onGroupAccordionSelectionChange: null,
  errorSelectionEmpty: null,
  updateErrorSelectionEmpty: () => { }
};
